import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import DefaultExtensionProvider from '@atlaskit/editor-common/extensions/default-extension-provider';
import type {
	ExtensionKey,
	ExtensionManifest,
	ExtensionType,
} from '@atlaskit/editor-common/extensions/extension-manifest';
import type { Parameters } from '@atlaskit/editor-common/extensions/extension-parameters';

import { KNOWLEDGE_CARD_MANIFEST_KEY } from '@atlassian/smart-knowledge-cards/knowledge-card-identity';
import { PEOPLE_ENTITY_MANIFEST_KEY } from '@atlassian/user-profile-preview/persisted-people-entity-identity';

export type KnowledgeCardRenderContext = {
	source: 'confluence-page';
	pageType: 'regular' | 'live';
	mode: 'edit' | 'view';
};

const getKnowledgeCardType = (key: string): 'linkList' | 'peopleEntity' | undefined => {
	if (key === KNOWLEDGE_CARD_MANIFEST_KEY) {
		return 'linkList';
	}
	if (key === PEOPLE_ENTITY_MANIFEST_KEY) {
		return 'peopleEntity';
	}
};

const reportKnowledgeCardExtensionFailure = (
	createAnalyticsEvent: CreateUIAnalyticsEvent | undefined,
	knowledgeCardType: 'linkList' | 'peopleEntity',
	failureStage: 'manifestLoad' | 'manifestLookup' | 'moduleLoad',
	context: KnowledgeCardRenderContext,
) => {
	createAnalyticsEvent?.({
		type: 'sendOperationalEvent',
		data: {
			source: context.source,
			action: 'failed',
			actionSubject: 'persistedKnowledgeCardExtension',
			attributes: {
				pageType: context.pageType,
				mode: context.mode,
				cardType: knowledgeCardType,
				failureStage,
			},
		},
	}).fire();
};

export const loadKnowledgeCardManifest = async <T>(
	load: () => Promise<T>,
	knowledgeCardType: 'linkList' | 'peopleEntity',
	context: KnowledgeCardRenderContext,
	createAnalyticsEvent?: CreateUIAnalyticsEvent,
): Promise<T> => {
	try {
		return await load();
	} catch (error) {
		reportKnowledgeCardExtensionFailure(
			createAnalyticsEvent,
			knowledgeCardType,
			'manifestLoad',
			context,
		);
		throw error;
	}
};

export const monitorKnowledgeCardModuleLoad = (
	manifest: ExtensionManifest,
	knowledgeCardType: 'linkList' | 'peopleEntity',
	context: KnowledgeCardRenderContext,
	createAnalyticsEvent?: CreateUIAnalyticsEvent,
): ExtensionManifest => {
	const reportedNodes = new Set<string>();
	return {
		...manifest,
		modules: {
			...manifest.modules,
			nodes: Object.fromEntries(
				Object.entries(manifest.modules.nodes ?? {}).map(([key, node]) => [
					key,
					{
						...node,
						render: async () => {
							try {
								return await node.render();
							} catch (error) {
								if (!reportedNodes.has(key)) {
									reportedNodes.add(key);
									reportKnowledgeCardExtensionFailure(
										createAnalyticsEvent,
										knowledgeCardType,
										'moduleLoad',
										context,
									);
								}
								throw error;
							}
						},
					},
				]),
			),
		},
	};
};

export const monitorKnowledgeCardManifestLookup = (
	provider: DefaultExtensionProvider<Parameters>,
	context: KnowledgeCardRenderContext,
	createAnalyticsEvent?: CreateUIAnalyticsEvent,
): DefaultExtensionProvider<Parameters> => {
	const getExtension = provider.getExtension.bind(provider);
	const reportedLookups = new Set<string>();
	provider.getExtension = async (type: ExtensionType, key: ExtensionKey) => {
		try {
			return await getExtension(type, key);
		} catch (error) {
			const knowledgeCardType = getKnowledgeCardType(key);
			if (knowledgeCardType && !reportedLookups.has(key)) {
				reportedLookups.add(key);
				reportKnowledgeCardExtensionFailure(
					createAnalyticsEvent,
					knowledgeCardType,
					'manifestLookup',
					context,
				);
			}
			throw error;
		}
	};
	return provider;
};
